export class CarouselNav {
	/**
	 * Initialize carousel navigation functionality
	 */
	constructor() {
		this.carousels = document.querySelectorAll("[data-carousel]");

		this.init();
	}

	/**
	 * Initialize all carousels
	 * @private
	 */
	init() {
		this.carousels.forEach((carousel) => this.setupCarousel(carousel));
	}

	/**
	 * Set up a single carousel
	 * @private
	 * @param {HTMLElement} carousel
	 */
	setupCarousel(carousel) {
		const scroller = carousel.querySelector("[data-carousel-scroller]");
		const prevButton = carousel.querySelector("[data-carousel-prev]");
		const nextButton = carousel.querySelector("[data-carousel-next]");

		if (!scroller || !prevButton || !nextButton) return;

		let hasOverflow = false;

		const checkOverflow = () => {
			hasOverflow = scroller.scrollWidth > scroller.clientWidth;
			carousel.toggleAttribute("data-scrollable", hasOverflow);
			updateButtons();
		};

		const updateButtons = () => {
			if (!hasOverflow) {
				prevButton.disabled = true;
				nextButton.disabled = true;
				return;
			}

			const { scrollLeft, scrollWidth, clientWidth } = scroller;
			prevButton.disabled = scrollLeft <= 0;
			nextButton.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
		};

		// Initial check
		checkOverflow();

		// Set up visibility detection for fading out-of-view cards
		this.setupVisibilityDetection(scroller);

		// Re-check when children change
		const mutationObserver = new MutationObserver(checkOverflow);
		mutationObserver.observe(scroller, { childList: true });

		// Update on scroll
		scroller.addEventListener("scroll", updateButtons, { passive: true });

		// Re-check on resize (children may reflow)
		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(scroller);

		// Button click handlers
		prevButton.addEventListener("click", () => {
			this.scroll(scroller, "prev");
		});

		nextButton.addEventListener("click", () => {
			this.scroll(scroller, "next");
		});
	}

	/**
	 * Set up scroll-linked opacity for cards based on visibility
	 * @private
	 * @param {HTMLElement} scroller
	 */
	setupVisibilityDetection(scroller) {
		const updateOpacity = () => {
			const scrollerRect = scroller.getBoundingClientRect();

			Array.from(scroller.children).forEach((card) => {
				const cardRect = card.getBoundingClientRect();

				// Calculate how much of the card is visible within scroller
				const visibleLeft = Math.max(cardRect.left, scrollerRect.left);
				const visibleRight = Math.min(cardRect.right, scrollerRect.right);
				const visibleWidth = Math.max(0, visibleRight - visibleLeft);
				const visibilityRatio = visibleWidth / cardRect.width;

				// Map visibility (0-1) to opacity (0.2-1), skip if reduced motion
				if (!this.isReducedMotion()) {
					const opacity = 0.2 + visibilityRatio * 0.8;
					card.style.opacity = opacity;
				} else {
					card.style.opacity = 1;
				}
			});
		};

		// Update on scroll
		scroller.addEventListener("scroll", updateOpacity, { passive: true });

		// Initial update
		updateOpacity();

		// Update on resize
		const resizeObserver = new ResizeObserver(updateOpacity);
		resizeObserver.observe(scroller);
	}

	isReducedMotion() {
		return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	}

	/**
	 * Scroll the carousel in the specified direction
	 * @private
	 * @param {HTMLElement} scroller
	 * @param {'prev'|'next'} direction
	 */
	scroll(scroller, direction) {
		const firstCard = scroller.children[0];
		if (!firstCard) return;

		// Calculate card width + gap
		const gap = parseFloat(getComputedStyle(scroller).gap) || 0;
		const cardWidth = firstCard.offsetWidth;
		const cardStep = cardWidth + gap;

		// Calculate visible cards, scroll by (visible - 1) to keep context
		const visibleCards = Math.floor(scroller.clientWidth / cardStep);
		const cardsToScroll = Math.max(1, visibleCards - 1);
		const scrollAmount = cardStep * cardsToScroll;

		const targetScroll =
			direction === "prev"
				? scroller.scrollLeft - scrollAmount
				: scroller.scrollLeft + scrollAmount;

		scroller.scrollTo({
			left: targetScroll,
			behavior: this.isReducedMotion() ? "instant" : "smooth",
		});
	}
}
